import React, { useCallback } from 'react'
import MaterialTable from '@material-table/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles'
import { tableAlternateLight } from '../../services/theme'
import { Card, CardContent, IconButton, CardHeader } from '@material-ui/core'
import { useSelector, useDispatch } from 'react-redux'
import { formatNumsToDollars, setDateToMidday } from '../../utils'
import { showError, showSuccess } from '../../actions/misc/misc.actions'
import moment from 'moment'
import { api } from '../../services/api'

const STRINGS = {
	Title: 'Special Day Pass Pricing - Dates',
	DateRange: 'Date Range',
	Description: 'Description',
	FirstPet: 'First Dog Price',
	SecondPet: 'Second Dog Price'
}

const MESSAGES = {
	Error: 'Error Removing Special Pricing',
	PastDates: 'Editing of past dates not allowed'
}

const useStyles = makeStyles(() => ({
	root: {
		maxWidth: '1245px'
	}
}))

const cellStyle = {
	paddingLeft: 0
}

const SpecialDayPassPricingTable = ({ setLocalLoading, setSpecialDayPassPrice }) => {
	const classes = useStyles()
	const dispatch = useDispatch()

	const selectedFacility = useSelector((state) => state.specific.facilities.selected)

	const haveDatesPassed = useCallback(
		({ start_date, end_date }) => {
			const startDate = setDateToMidday(moment(start_date))
			const endDate = setDateToMidday(moment(end_date))
			const now = setDateToMidday(moment())

			const startDayDiff = now.diff(startDate)
			const endDayDiff = now.diff(endDate)

			const past = startDayDiff > 0 || endDayDiff > 0

			if (past) {
				dispatch(showError(MESSAGES.PastDates))
			}

			return past
		},
		[dispatch]
	)

	const handleDelete = useCallback(
		(tableData) => {
			const { _id } = tableData

			const deletePricing = async () => {
				try {
					setLocalLoading(true)
					const { data } = await api.patch(
						`/facilities/specialDayPassPrice/${selectedFacility._id}/delete`,
						{
							_id
						}
					)

					if (data.errors.length > 0) {
						dispatch(showError(data.errors[0]))
						return
					}

					const deletedId = data.data[0]

					const special_daypass_prices = selectedFacility.special_daypass_prices.filter(
						({ _id: id }) => id !== deletedId
					)

					dispatch(showSuccess(data.messages[0]))
					dispatch({
						type: 'UPDATE_FACILITY_SUCCESS',
						payload: {
							...selectedFacility,
							special_daypass_prices
						}
					})
				} catch (e) {
					dispatch(showError(MESSAGES.Error))
					console.log('big ol e...', e)
				} finally {
					setLocalLoading(false)
				}
			}

			if (selectedFacility) {
				deletePricing()
			}
		},
		[selectedFacility]
	)

	return (
		<Card className={classes.root}>
			<CardHeader title={STRINGS.Title} />
			<CardContent>
				<MuiThemeProvider theme={tableAlternateLight}>
					<MaterialTable
						isLoading={false}
						style={{
							borderRadius: '0px',
							boxShadow: 'none',
							borderBottom: '1px solid #D0CFD2',
							marginBottom: '16px'
						}}
						onRowClick={(event, rowData) => {
							const {
								tableData: { id }
							} = rowData
							const record = selectedFacility?.special_daypass_prices[id] || null
							if (record) {
								const { start_date, end_date } = record
								if (!haveDatesPassed({ start_date, end_date })) {
									setSpecialDayPassPrice(record)
								} else {
									setSpecialDayPassPrice(null)
								}
							}
						}}
						options={{
							toolbar: false,
							headerStyle: {
								color: '#1A1A35',
								textTransform: 'uppercase',
								fontWeight: 800,
								fontSize: '12px',
								lineHeight: '16px',
								padding: '8px 0px',
								borderBottom: '1px solid #D0CFD2'
							},
							actionsColumnIndex: -1,
							rowStyle: {
								backgroundColor: '#FFFFFF'
							},
							paging: false,
							search: false,
							actionsCellStyle: {
								padding: '6px 0px'
							}
						}}
						localization={{
							header: {
								actions: ''
							}
						}}
						actions={[{}]}
						columns={[
							{
								title: STRINGS.DateRange,
								field: 'start_date',
								cellStyle,
								render: (data) => {
									const { start_date, end_date } = data
									const startDate = moment(start_date).format('MMM D, YYYY')
									const endDate = moment(end_date).format('MMM D, YYYY')
									if (startDate === endDate) {
										return <div>{startDate}</div>
									}
									return (
										<span>
											{startDate} - {endDate}
										</span>
									)
								}
							},
							{
								title: STRINGS.Description,
								field: 'description',
								cellStyle
							},
							{
								title: STRINGS.FirstPet,
								field: 'day_pass_price',
								cellStyle,
								render: (data) => {
									const { day_pass_price } = data
									return <span>{formatNumsToDollars(day_pass_price)}</span>
								}
							},
							{
								title: STRINGS.SecondPet,
								field: 'additional_pet_price',
								cellStyle,
								render: (data) => {
									const { additional_pet_price } = data
									return <span>{formatNumsToDollars(additional_pet_price)}</span>
								}
							}
						]}
						components={{
							Action: ({ data }) => {
								return (
									<IconButton
										onClick={(e) => {
											e.stopPropagation()
											handleDelete(data)
										}}>
										<DeleteIcon fontSize="small" />
									</IconButton>
								)
							}
						}}
						data={selectedFacility?.special_daypass_prices || []}
					/>
				</MuiThemeProvider>
			</CardContent>
		</Card>
	)
}

export default SpecialDayPassPricingTable
